import { useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import dragImg from "../../assets/img/Black.svg";
import Text from "../Text";
import imageCompression from "browser-image-compression";

const Form = styled("form")`
  width: 100%;
  text-align: center;
  position: relative;
  margin: auto;
`;

const Input = styled("input")`
  display: none;
`;

const Label = styled("label")(({ theme, multiple }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#3C4B64",
  textAlign: "center",
  fontWeight: 400,
  fontSize: 12,
  borderRadius: 4,
  border: `1px dashed ${theme.color.gray50}`,
  background: theme.color.gray10,
  width: 112,
  height: 130,
}));

const Button = styled("button")(({ theme }) => ({
  cursor: "pointer",
  border: "none",
  width: "100%",
  height: "100%",
  background: theme.color.gray10,
}));

const Error = styled("div")`
  margin-top: 1rem;
  font-size: 12px;
  color: red;
`;

const ActiveDrag = styled("div")`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 1rem;
  top: 0px;
  right: 0px;
  bottom: 0px;
  left: 0px;
`;

const AddButton = styled("div")`
  width: 36;
  height: 36;
  background: rgba(217, 214, 230, 1);
  color: #ffffff;
  border-radius: 100;
  text-align: center;
  font-size: 30;
`;

// drag drop file component
const DragDropFile = ({
  onDragAndDrop,
  accept,
  multiple,
  limit,
  current,
  isMobile,
  valid = true
}) => {
  const { t } = useTranslation("upload_files");
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");

  const inputRef = useRef(null);
  const theme = useTheme();

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const compressImages = async (files) => {
    const compressedFiles = [];
    let rejected = false;

    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        compressedFiles.push(file);
        continue;
      }

      try {
        const compressed = await imageCompression(file, {
          maxSizeMB: 0.5,
          maxWidthOrHeight: 800,
          useWebWorker: true,
        });

        if (compressed.size > 512 * 1024) {
          setError(t("too_large"));
          rejected = true;
          continue;
        }

        compressedFiles.push(compressed);
      } catch (err) {
        console.error("Compression failed:", err);
        setError(`Compression failed for ${file.name}`);
        rejected = true;
      }
    }

    return rejected ? null : compressedFiles;
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    const compressed = await compressImages(files);
    onDragAndDrop(compressed);
  };

  const handleChange = async (e) => {
    e.preventDefault();

    const files = Array.from(e.target.files);
    const compressed = await compressImages(files);
    onDragAndDrop(compressed);
  };

  const onButtonClick = () => {
    inputRef.current.click();
  };

  return (
    <Form
      id="form-file-upload"
      onDragEnter={handleDrag}
      onSubmit={(e) => e.preventDefault()}

    >
      <Input
        ref={inputRef}
        type="file"
        id="input-file-upload"
        multiple={multiple}
        onChange={handleChange}
        accept={accept.join(",")}

      />
      {isMobile && (
        <AddButton
          style={{
            width: 36,
            height: 36,
            background: "rgba(217, 214, 230, 1)",
            color: "#ffffff",
            borderRadius: 100,
            textAlign: "center",
            fontSize: 30,
            border: !valid ? `1px solid ${theme.color.error}` : ``,

          }}
          onClick={onButtonClick}
        >
          +
        </AddButton>
      )
      }
      {
        !isMobile && (
          <Label
            id="label-file-upload"
            htmlFor="input-file-upload"
            multiple={multiple}
            style={{ border: !valid ? `1px solid ${theme.color.error}` : ``, }}
          >
            <Button onClick={onButtonClick}>
              <div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",

                  }}
                >
                  <img
                    alt="drag"
                    src={dragImg}
                    style={{ width: 32, height: 32 }}
                  />
                </div>
                {!isMobile && (
                  <div>
                    <Text.SR>{t("drag_and_drop")}</Text.SR>
                  </div>
                )}
              </div>
            </Button>
          </Label>
        )
      }

      {
        current === 0 && !isMobile && (
          <Text size={12} color={theme.color.navy} style={{ lineHeight: "40px" }}>
            {t("pics_limit", {
              limit: limit,
            })}
          </Text>
        )
      }
      {!!error && <Error>{error}</Error>}
      {
        limit > current && current !== 0 && !isMobile && (
          <Text
            size={12}
            color={theme.color.navy}
            style={{ lineHeight: isMobile ? "12px" : "40px" }}
          >
            {t("pics_left_till_limit", {
              num: limit - current,
            })}
          </Text>
        )
      }
      {
        dragActive && (
          <ActiveDrag
            id="drag-file-element"
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          ></ActiveDrag>
        )
      }
    </Form >
  );
};

export default DragDropFile;
